import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { Film, Play, User, Search, RotateCcw } from 'lucide-react';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import { jubileeStorageService } from '../services/jubileeStorageService';

export default function JubileeVideoGallery({ initialFilter = {} }) {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(initialFilter.year || 'all');
  const [selectedEvent, setSelectedEvent] = useState(initialFilter.eventId || 'all');
  const [selectedCategory, setSelectedCategory] = useState(initialFilter.categoryId || 'all');

  const allVideos = useMemo(() => jubileeStorageService.getMediaByType('video'), []);
  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const categories = useMemo(() => jubileeStorageService.getCategories(), []);

  // Filtered videos
  const filteredVideos = useMemo(() => {
    return jubileeStorageService.searchMedia({
      mediaType: 'video',
      query: searchQuery,
      year: selectedYear,
      eventId: selectedEvent,
      categoryId: selectedCategory,
    });
  }, [searchQuery, selectedYear, selectedEvent, selectedCategory]);

  const handleOpenVideo = (video) => {
    setSelectedVideo(video);
    setShowPlayer(true);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedYear('all');
    setSelectedEvent('all');
    setSelectedCategory('all');
  };

  const hasActiveFilters = searchQuery !== '' || selectedYear !== 'all' || selectedEvent !== 'all' || selectedCategory !== 'all';

  // Available unique years
  const availableYears = Array.from(new Set(allVideos.map((v) => v.year))).sort((a, b) => b - a);

  return (
    <div className="py-4">
      <Container>
        {/* Gallery Header */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="p-2 rounded-3 bg-danger text-white d-inline-flex">
                <Film size={22} />
              </div>
              <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                Video Gallery
              </h2>
            </div>
            <p className="text-muted small m-0">
              Watch Jubilee documentaries, historic convocation recordings, and liturgical celebrations.
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25 px-3 py-2 rounded-pill fw-semibold">
              {filteredVideos.length} {filteredVideos.length === 1 ? 'Video' : 'Videos'} Available
            </span>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white p-3 rounded-4 shadow-sm border mb-4">
          <Row className="g-2 align-items-center">
            <Col xs={12} md={4}>
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-light border-end-0">
                  <Search size={15} className="text-muted" />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search videos by title, speaker, tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-start-0"
                />
              </div>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedEvent}
                onChange={(e) => setSelectedEvent(e.target.value)}
              >
                <option value="all">All Events</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2}>
              <Form.Select
                size="sm"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="all">All Years</option>
                {availableYears.map((y) => (
                  <option key={y} value={y}>
                    Year {y}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={6} md={2} className="text-end">
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleClearFilters}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                >
                  <RotateCcw size={13} /> Clear Filters
                </Button>
              )}
            </Col>
          </Row>
        </div>

        {/* Video Grid */}
        {filteredVideos.length > 0 ? (
          <Row className="g-4">
            {filteredVideos.map((video) => (
              <Col key={video.id} xs={12} sm={6} lg={4}>
                <div
                  className="jubilee-media-card cursor-pointer"
                  onClick={() => handleOpenVideo(video)}
                >
                  <div className="media-thumbnail-wrapper">
                    <img
                      src={video.thumbnailUrl}
                      alt={video.title}
                      className="media-thumbnail-img"
                      loading="lazy"
                    />
                    <span className="media-type-badge video">
                      <Film size={12} /> Video
                    </span>
                    {video.duration && (
                      <span className="media-duration-badge">{video.duration}</span>
                    )}
                    <div className="media-play-overlay">
                      <div className="play-circle-btn">
                        <Play size={22} fill="#0f172a" style={{ marginLeft: '3px' }} />
                      </div>
                    </div>
                  </div>

                  <div className="media-card-body">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="badge bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" style={{ fontSize: '0.72rem' }}>
                        {video.year}
                      </span>
                      {video.categoryName && (
                        <span className="badge bg-light text-secondary border text-truncate" style={{ fontSize: '0.72rem', maxWidth: '140px' }}>
                          {video.categoryName}
                        </span>
                      )}
                    </div>

                    <h5 className="media-card-title">{video.title}</h5>
                    <p className="media-card-desc">{video.description}</p>

                    {video.speaker && (
                      <div className="small text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.78rem' }}>
                        <User size={13} className="text-primary" />
                        <span className="text-truncate">{video.speaker}</span>
                      </div>
                    )}

                    <div className="media-card-meta">
                      <span className="text-truncate" style={{ maxWidth: '180px' }}>
                        {video.eventName || 'Jubilee Archive'}
                      </span>
                      <span className="fw-semibold text-danger small">Watch Video →</span>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        ) : (
          <div className="text-center py-5 bg-white rounded-4 border my-4">
            <div className="p-3 bg-danger bg-opacity-10 text-danger rounded-circle d-inline-flex mb-3">
              <Film size={36} />
            </div>
            <h5 className="fw-bold text-dark">No videos available for this selection</h5>
            <p className="text-muted small mb-3">
              Try adjusting your search criteria or clear the filters.
            </p>
            {hasActiveFilters && (
              <Button variant="outline-danger" size="sm" onClick={handleClearFilters}>
                Clear All Filters
              </Button>
            )}
          </div>
        )}
      </Container>

      {/* Video Player Modal */}
      <JubileeVideoPlayerModal
        video={selectedVideo}
        show={showPlayer}
        onHide={() => setShowPlayer(false)}
        onSelectVideo={(newVideo) => setSelectedVideo(newVideo)}
        allVideos={allVideos}
      />
    </div>
  );
}
